import { gsap } from 'gsap';
import {
  AnimatePageModel,
  PageTimeline,
  ProjectModel
} from '@desygner/api-animations-models';
import {
  AnimationMap,
  utils as animationUtils
} from '@desygner/ui-common-animation-effects';
import {
  convertToSeconds,
  getBBox,
  isAnimationValid,
  isFunction,
  prepareGroupAnimations
} from './utils';

export const DEFAULT_DELAY = 0.3; // seconds
export const DEFAULT_IN_ANIMATION_DURATION = 1; // seconds
export const DEFAULT_OUT_ANIMATION_DURATION = 1; // seconds

export const DEFAULT_PAGE = 1; // seconds

export function getInDurationInSec(animate: { inDuration?: number, inOutDuration?: number }) {
  return convertToSeconds(
    animate.inDuration ?? animate.inOutDuration,
    DEFAULT_IN_ANIMATION_DURATION
  );
}

export function getOutDurationInSec(animate: { outDuration?: number, inOutDuration?: number }) {
  return convertToSeconds(
    animate.outDuration ?? animate.inOutDuration,
    DEFAULT_IN_ANIMATION_DURATION
  );
}

export function countAnimatedElements(
  animate: AnimatePageModel,
  elements: Element[]
): number {
  const hasElementAnimations =
    animate.elementAnimations && Object.keys(animate.elementAnimations).length;

  const hasPageAnimation =
    animate.animation !== 'no_animation' && animate.animation && 
    isFunction(AnimationMap[animate.animation]);

  if (!hasElementAnimations && !hasPageAnimation) {
    return 0;
  }

  let animatedElementCount = 0;

  for (const element of elements) {
    const id = element.id || element.getAttribute('data-id') || 0;
    const animationType = hasElementAnimations
      ? animate.elementAnimations?.[id] ?? animate.animation
      : animate.animation;

    if (!animationType) continue;

    const animation = AnimationMap[animationType];

    if (!animation) continue;

    animatedElementCount++;
  }

  return animatedElementCount;
}

// calculateAnimationDelay returns an animation's position in a timeline (i.e. when should the animation be played in relative to the start of the timeline)
// see this: https://gsap.com/docs/v3/GSAP/Timeline/add()#positioning-animations-in-a-timeline
function calculateAnimationDelay(
  isAnimatedTogether: boolean,
  onlyAnimateSelectedElement: boolean
): number | string {
  if (isAnimatedTogether || onlyAnimateSelectedElement) return 0; // animation will be played right at the start of the timeline

  return '>';
}

function getTargetDuration(
  animate: AnimatePageModel,
  totalDuration: number,
  animatedElementCount: number
): number {
  return animate.animateElementsTogether ? totalDuration : totalDuration / animatedElementCount;
}

function getDuration(
  inDurationInSec: number,
  stayDurationInSec: number,
  outDurationInSec: number,
  onlyAnimateSelectedElement: boolean,
  yoyo: boolean
): number {
  return inDurationInSec + (onlyAnimateSelectedElement ? 0 : stayDurationInSec) + (yoyo ? outDurationInSec : 0);
}

export function generateTimeline(
  animate: AnimatePageModel,
  svg: SVGSVGElement,
  options?: {
    yoyo?: boolean; // https://gsap.com/docs/v3/GSAP/Tween/yoyo(),
    onlyAnimateSelectedElement?: boolean;
    initiateVideoPlay?: boolean;
  }
): PageTimeline {
  const { yoyo, onlyAnimateSelectedElement, initiateVideoPlay = true } = options ?? {};
  const svgElement = prepareGroupAnimations(svg, animate);
  const elements = getSortedElements(svgElement);
  const hasElementAnimations =
    animate.elementAnimations &&
    Object.keys(animate.elementAnimations).length > 0;

  const hasPageAnimation =
    animate.animation !== 'no_animation' && animate.animation &&
    isFunction(AnimationMap[animate.animation]);

  const animatedElementCount = countAnimatedElements(animate, elements);
  const inAnimationDurationInSec = getInDurationInSec(animate);

  const outAnimationDurationInSec = getOutDurationInSec(animate);

  const pageDurationInSec = convertToSeconds(
    animate.pageDuration,
    DEFAULT_PAGE
  );

  const duration = getDuration(inAnimationDurationInSec, pageDurationInSec, outAnimationDurationInSec, !!onlyAnimateSelectedElement, !!yoyo);
  const parentTimeline = gsap.timeline({
    duration: duration,
    paused: false
  });

  const inTimeline = gsap.timeline({
    paused: false
  });

  const outTimeline = gsap.timeline({
    paused: false
  });

  if (!hasElementAnimations && !hasPageAnimation) {
    console.log(`No animation found ${animate.animation}`);
    return;
  }

  elements.forEach((element) => {
    if (
      onlyAnimateSelectedElement &&
      !element.classList.contains('selected') &&
      element.querySelectorAll('.selected').length === 0
    ) {
      return;
    }

    const id = element.id || element.getAttribute('data-id') || 0;
    const animationType = hasElementAnimations
      ? animate.elementAnimations?.[id] ?? animate.animation
      : animate.animation;

    const elementWrapper = animationUtils.createAnimationContainer(element);

    if (!animationType) return;

    const inAnimation = AnimationMap[animationType]?.(elementWrapper);

    if (!inAnimation) {
      return;
    }

    const expectedInElementDuration = getTargetDuration(animate, inAnimationDurationInSec, animatedElementCount);
    const animationDelay = calculateAnimationDelay(
      !!animate.animateElementsTogether,
      !!onlyAnimateSelectedElement
    );

    inAnimation.timeScale(inAnimation.duration() / expectedInElementDuration);
    inTimeline.add(inAnimation, animationDelay);
    if (yoyo) {
      const outAnimation = AnimationMap[animationType]?.(elementWrapper);

      if (!outAnimation) {
        return;
      }

      const expectedOutElementDuration = getTargetDuration(animate, outAnimationDurationInSec, animatedElementCount);

      const animationDelay = calculateAnimationDelay(
        !!animate.animateElementsTogether,
        !!onlyAnimateSelectedElement
      );

      outAnimation.timeScale(outAnimation.duration() / expectedOutElementDuration);
      outTimeline.add(outAnimation, animationDelay);
    }
  });

  inTimeline.timeScale(inTimeline.duration() / inAnimationDurationInSec);
  outTimeline.timeScale(outTimeline.duration() / outAnimationDurationInSec);

  const stayTimeline = gsap.timeline({
    duration: pageDurationInSec,
    paused: false,
    onStart: function() {
      if (initiateVideoPlay === false) return;

      const allVideoElements = [...svg.querySelectorAll<HTMLElement>('.video')];

      allVideoElements.forEach((videoElement) => {
        const video = videoElement.querySelector('video');
        const thumbnail = videoElement.querySelector('image');

        if (video) {
          if (thumbnail) {
            thumbnail.style.opacity = '0';
          }

          video.play();
        }
      });
    },
    onComplete: function() {
      const allVideoElements = [...document.querySelectorAll<HTMLElement>('.video')];

      allVideoElements.forEach((videoElement) => {

        const video = videoElement.querySelector('video');
        const thumbnail = videoElement.querySelector('image');

        if (video) {
          if (thumbnail) {
            thumbnail.style.opacity = '0';
          }

          video.pause();
        }
      });
    }
  });

  stayTimeline.timeScale(stayTimeline.duration() / pageDurationInSec);

  parentTimeline.add(inTimeline, '>');
  if (!onlyAnimateSelectedElement) {
    parentTimeline.add(stayTimeline, '>');
  }

  if (yoyo) {
    parentTimeline.add(outTimeline.reverse(), '>');
  }

  parentTimeline.timeScale(parentTimeline.duration() / duration);
  return parentTimeline;
}

export function addVideo(content: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');

  [...doc.querySelectorAll('.video')].forEach((videoElement) => {
    const videoSrc = videoElement.getAttribute('xlink:href') ?? '';
    const thumbnail = videoElement.querySelector('image');

    if (!thumbnail) {
      return;
    }

    const x = thumbnail.getAttribute('x') ?? '0';
    const y = thumbnail.getAttribute('y') ?? '0';
    const width = thumbnail.getAttribute('width') ?? '0';
    const height = thumbnail.getAttribute('height') ?? '0';
    const videoWrapper = document.createElement('foreignObject') ?? '0';

    videoWrapper.setAttribute('x', x);
    videoWrapper.setAttribute('y', y);
    videoWrapper.setAttribute('width', width);
    videoWrapper.setAttribute('height', height);
    const video = document.createElement('video');

    video.setAttribute('width', width);
    video.setAttribute('height', height);
    video.setAttribute('crossorigin', '*');
    video.setAttribute('id', `${videoElement.getAttribute('id') ?? ''}-video`);
    video.classList.add('animate-video');
    const sourceElement = document.createElement('source');

    sourceElement.setAttribute('src', videoSrc);
    video.appendChild(sourceElement);
    videoWrapper.appendChild(video);
    thumbnail.insertAdjacentElement('beforebegin', videoWrapper);
  });

  return doc.body.firstElementChild?.outerHTML ?? '';
}

export function mountSvg(
  elementTarget: Element,
  page: {
    id: number;
    content: string;
  }
): SVGSVGElement {
  const oldSvgElement = document.querySelector<HTMLDivElement>(`#svg-${page.id}-container`);
  const svgElement = oldSvgElement || document.createElement('div');
  const content = addVideo(page.content);

  svgElement.style.position = 'absolute';
  svgElement.innerHTML = content;
  svgElement.setAttribute('id', `svg-${page.id}-container`);
  svgElement.style.height = '100%';
  svgElement.style.marginLeft = 'auto';
  svgElement.style.marginRight = 'auto';
  svgElement.style.left = '0';
  svgElement.style.right = '0';
  elementTarget.appendChild(svgElement);

  const svg = <SVGSVGElement>(
    elementTarget.querySelector(`#svg-${page.id}-container`)?.firstElementChild
  );
  const svgHeight = parseFloat(svg.getAttribute('height')!);
  const svgWidth = parseFloat(svg.getAttribute('width')!);

  svg.setAttribute('id', `svg-${page.id}`);
  svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.style.display = 'none';

  const clipPath = document.createElementNS(
    'http://www.w3.org/2000/svg',
    'clipPath'
  );

  clipPath.setAttribute('id', `clip-${page.id}`);

  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');

  rect.setAttribute('x', '0');
  rect.setAttribute('y', '0');
  rect.setAttribute('width', String(svgWidth));
  rect.setAttribute('height', String(svgHeight));

  clipPath.appendChild(rect);
  svg.appendChild(clipPath);

  svg
    .querySelector('.main')
    ?.setAttribute('clip-path', `url(#clip-${page.id})`);

  svg
    .querySelector('.background')
    ?.setAttribute('clip-path', `url(#clip-${page.id})`);

  return svg;
}

export function createProjectTimeline(
  elementTarget: Element,
  metadata: ProjectModel,
  pages: {
    id: number;
    content: string;
  }[],
  options?: {
    yoyo?: boolean;
    paused?: boolean;
  }
) {
  const timeline = gsap.timeline({ paused: !!options?.paused });

  pages.forEach(function(page) {
    const { id } = page;
    const metadataPage = metadata?.pages?.[id];

    if (!metadataPage) {
      return;
    }

    const svg = mountSvg(elementTarget, { ...page, content: page.content });
    const pageTimeline = generateTimeline(metadataPage, svg, {
      yoyo: !!options?.yoyo,
      onlyAnimateSelectedElement: false
    });

    if (pageTimeline) {
      timeline.add(() => {
        svg.style.display = 'block';
      }, `>`);

      timeline.add(pageTimeline, '>');

      timeline.add(() => {
        svg.style.display = 'none';
      }, `>`);
    }
  });

  return timeline;
}

function getSortedElements(
  svg: SVGSVGElement,
  selector = '#svg_background_image, .main > .element'
): Element[] {
  // Need to clone the SVG and append to document to get the correct bounding box
  const clonedSVG = document.body.appendChild(svg.cloneNode(true) as SVGSVGElement);

  clonedSVG.style.display = 'block';
  clonedSVG.style.visibility = 'hidden';
  clonedSVG.style.position = 'absolute';
  clonedSVG.style.left = '-9999px';

  const pageElements: Element[] = [...svg.querySelectorAll(selector)];
  const clonedPageElements: Element[] = [...clonedSVG.querySelectorAll(selector)];

  const targetBoxes = pageElements.reduce((acc, element, index) => {
    const box = getBBox(clonedPageElements[index]);
    const id = element.id || element.getAttribute('data-id') || 0;

    if (box) {
      acc[id] = { x: box.x, y: box.y };
    }

    return acc;
  }, {} as Record<string, { x: number; y: number }>);

  pageElements.sort((a, b) => {
    const aId = a.id || a.getAttribute('data-id') || 0;
    const bId = b.id || b.getAttribute('data-id') || 0;

    const aBoundingBox = targetBoxes[aId];
    const bBoundingBox = targetBoxes[bId];

    // If either bounding box is undefined, keep original order
    if (!aBoundingBox || !bBoundingBox) {
      return 0;
    }

    return sortByDistanceFromTopLeftCorner(aBoundingBox, bBoundingBox);
  });

  document.body.removeChild(clonedSVG);

  return pageElements;
}

type Position = { x: number; y: number };

function sortByDistanceFromTopLeftCorner(
  { x: ax, y: ay }: Position,
  { x: bx, y: by }: Position
) {
  return ay - by || ax - bx;
}

export type CreateAnimationTimelineParams = {
  animate: AnimatePageModel;
  svg: SVGSVGElement;
  duration: number;
  onlyAnimateSelectedElement?: boolean;
}

export function createAnimationTimeline({ animate, svg, duration: totalDurationInSec, onlyAnimateSelectedElement = false }: CreateAnimationTimelineParams) {
  const timeline = gsap.timeline({ paused: true });

  const svgElement = prepareGroupAnimations(svg, animate);
  const elements = getSortedElements(svgElement);
  const animatedElementCount = countAnimatedElements(animate, elements);

  elements.forEach((element) => {
    if (
      onlyAnimateSelectedElement &&
      !element.classList.contains('selected') &&
      element.querySelectorAll('.selected').length === 0
    ) {
      return;
    }

    const id = element.id || element.getAttribute('data-id') || 0;
    const animationType = animate.elementAnimations?.[id] ?? animate.animation;

    if (!animationType) return;

    const animationFactory = AnimationMap[animationType];

    if (!animationFactory) return;

    const elementWrapper = animationUtils.createAnimationContainer(element);
    const animation = animationFactory(elementWrapper);

    if (!animation) return;

    const expectedElementDuration = getTargetDuration(animate, totalDurationInSec, animatedElementCount);

    if (animation.duration() > 0 && expectedElementDuration > 0) {
      animation.timeScale(animation.duration() / expectedElementDuration);
    }

    const animationDelay = calculateAnimationDelay(
      !!animate.animateElementsTogether,
      !!onlyAnimateSelectedElement
    );

    timeline.add(animation, animationDelay);
  });

  if (timeline.duration() > 0 && totalDurationInSec > 0) {
    timeline.timeScale(timeline.duration() / totalDurationInSec);
  }

  return timeline;
}


export type CallAnimation = {
  animate: AnimatePageModel;
  svg: SVGSVGElement;
  onlyAnimateSelectedElement?: boolean;
};


export function callInAnimation({ animate, svg, onlyAnimateSelectedElement = false }: CallAnimation) {
  if (!isAnimationValid(animate)) {
    return null;
  }

  const duration = getInDurationInSec(animate);
  const timeline = createAnimationTimeline({
    animate,
    svg,
    duration,
    onlyAnimateSelectedElement,
  });

  return timeline.play(0);
}

export function callOutAnimation({ animate, svg, onlyAnimateSelectedElement = false }: CallAnimation) {
  if (!isAnimationValid(animate)) {
    return null;
  }

  const duration = getOutDurationInSec(animate);
  const timeline = createAnimationTimeline({
    animate,
    svg,
    duration,
    onlyAnimateSelectedElement,
  });


  return timeline.reverse(0); 
}
